import { load } from 'react-cookies'
import { loadSessionField } from '../auth/sessionProfileStore'
import documentTypes from '../flags_variants/document_types.json'
import dayjs from '../utils/dayjsConfig'
import { TASK_TYPES } from '../pos/constants/constants'

export const getLocale = () => loadSessionField('intl_locale') ?? load('intl_locale') ?? 'en-US'

export const getCurrency = () => loadSessionField('intl_currency') ?? load('intl_currency') ?? 'USD'

export const getDiscountPrice = (price, discount) => {
  discount > 0 ? price - price * (discount / 100) : price
}

export const capitalizeFirstLetterFrom = (sentence = '') => {
  if (!sentence) return ''
  return sentence.charAt(0).toUpperCase() + sentence.slice(1)
}

export const capitalizeFirstLetter = (word = '') => word.charAt(0).toUpperCase() + word.slice(1)

export const formatPrice = (price) => `P${price}`

export const formatDate = (date) => {
  const locale = getLocale()
  return new Date(date).toLocaleDateString(locale)
}

export const replaceAccentedCharacters = (text) => {
  return String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

export const normalizeFilterSearch = (text) => {
  return replaceAccentedCharacters(text).replace(/[\s.]+/g, '')
}

export const formatDiscount = (data, decimals = 5) => {
  const value = parseFloat(data)
  if (value > 100 || value < 0) return 0

  const regex = new RegExp(`(\\.\\d{${decimals}})\\d+`, 'g')
  const validDiscountNumber = data.replace(regex, '$1')
  return parseFloat(validDiscountNumber) || 0
}

export const formatMoney = (amount) => {
  const locale = getLocale()
  const currency = getCurrency()
  const totalAmount = Number(amount).toFixed(2)
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(totalAmount)
}

export const isBlankValue = (value) =>
  value === null || value === undefined || value === ''

export const valueOrDash = (value) => (isBlankValue(value) ? '-' : value)

export const valueOrZero = (value) => value ?? 0

export const formatMoneyOrDash = (value) =>
  isBlankValue(value) ? '-' : formatMoney(value)

export const formatShortDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return '-'

  const start = dayjs(startDate)
  const end = dayjs(endDate)

  if (!start.isValid() || !end.isValid()) return `${startDate} - ${endDate}`

  return `${start.format('DD MMM')} - ${end.format('DD MMM')}`
}

export const formatMoneyUpToSixDecimals = (amount) => {
  const locale = getLocale()
  const currency = getCurrency()
  const totalAmount = Number(amount || 0)
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 6
  }).format(totalAmount)
}

export const formatDiscountPercent = (discount = 0) => {
  const value = Number(discount || 0)
  return value === 0 ? '-' : `${value.toFixed(2)}%`
}

export const isGeofenceLocationDataNA = (clientLocation, latitude) => {
  return (
    !clientLocation ||
    Number(clientLocation) === 0 ||
    !latitude ||
    Number(latitude) === 0 ||
    latitude === '0.0'
  )
}

export const effectiveLocationStr = (effectiveLocation, clientLocation, latitude) => {
  if (isGeofenceLocationDataNA(clientLocation, latitude)) {
    return 'N/A'
  }
  return effectiveLocation ? 'Sí' : 'No'
}
export const effectiveLocationWithAllowedLocation = (effectiveLocation, hasAllowedLocation) => {
  if (!hasAllowedLocation) return 'N/A'
  if (effectiveLocation) return 'Sí'
  return 'No'
}

export const effectiveLocation = (effectiveLocation) => {
  if (effectiveLocation) return 'Sí'
  return 'No'
}

export const safePercent = (value, of) => {
  const validValue = Number(value)
  const validOf = Number(of)
  if (!Number.isFinite(validValue) || !Number.isFinite(validOf)) return 0
  const result = validOf === 0 ? 0 : Math.round((validValue / validOf) * 100)
  return Math.min(Number.isFinite(result) ? result : 0, 100)
}

export const endsVowel = (str) => 'aeiou'.includes(str.slice(-1))

export const parseDocumentType = (documentType) => documentTypes[documentType] || documentType

export const replaceWithBr = (str) => str.replace(/\n/g, '<br />')

export const clearCoordinates = (coord) => {
  if (!coord) {
    return null
  }
  let newCoord = parseFloat(coord)
  if (isNaN(newCoord)) {
    return null
  }
  if (newCoord === 0) {
    return null
  }
  return coord
}

export const getLastActivityTime = (data) => {
  if (!data.length || !data[0].created_date) {
    return '-'
  }

  const lastActivityDate = new Date(data[0].created_date)

  if (isNaN(lastActivityDate)) {
    return '-'
  }

  let hours = lastActivityDate.getHours()
  const minutes = String(lastActivityDate.getMinutes()).padStart(2, '0')
  const suffix = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12 || 12

  return `${hours}:${minutes} ${suffix}`
}

export const assignClientIndicesAndFilter = (data) => {
  let currentIndex = 0
  let lastClientUID = null

  const processedData = data
    .reduceRight((resultArray, currentItem) => {

      if (currentItem.client_uid !== lastClientUID) {
        lastClientUID = currentItem.client_uid
        currentIndex++
      }

      resultArray.push({
        ...currentItem,
        client_index: currentIndex
      })

      return resultArray
    }, [])
    .reverse()

  return processedData
}

export const allCoordinatesValid = (data) => {
  return data.every((item) => {
    const lat = item.latitude
    const long = item.longitude
    return (
      (lat === '0.0' && long === '0.0') ||
      (lat === 0 && long === 0) ||
      lat === null ||
      long === null
    )
  })
}

export const allCoordinatesValidClient = (item) =>
  isGeofenceLocationDataNA(item.client_latitude, item.latitude)

export const formatDateNoHours = (dateString) => {
  const locale = getLocale()
  const date = new Date(`${dateString}T00:00:00`)
  if (isNaN(date)) {
    return dateString
  }
  const options = {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric'
  }
  return date.toLocaleDateString(locale, options)
}

export const convertByteToMb = (imgBytes) => {
  const mb = imgBytes ? (Number(imgBytes) / 1024 / 1024).toFixed(1) : '0.0'
  return `${mb}MB`
}

export const calculateTaxRate = () => {
  let total = 0
  this.state.activity.details.map(
    (obj) => (total += (parseFloat(obj.extended_price) * parseFloat(obj.tax_rate)) / 100)
  )
  return total
}

export const calculateTotals = (cashData) => {
  return Object.keys(cashData)
    .sort((a, b) => parseFloat(b) - parseFloat(a))
    .map((denomination) => {
      const quantity = cashData[denomination] || 0
      const total = quantity * parseFloat(denomination)
      return {
        denomination: parseFloat(denomination),
        quantity,
        total
      }
    })
}

export const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export const isNullOrEmpty = (str) => {
  return !str?.trim()
}

export const getFormattedDate = (createdDate) => {
  if (!createdDate) return 'N/A'
  return new Date(createdDate).toLocaleString()
}

export const formatHour = (value) => {
  return value === '-' ? '-' : dayjs(value).format('hh:mm A')
}

export const formatVisitDateOrHour = (value) => {
  if (!value || value === '-') return '-'

  const visitDate = dayjs(value)
  if (!visitDate.isValid()) return value

  return visitDate.isSame(dayjs(), 'day')
    ? visitDate.format('hh:mm A')
    : visitDate.format('DD/MM/YYYY')
}

export const extractActiveRoles = (rolesString) => {
  if (!rolesString || typeof rolesString !== 'string') return []

  return Array.from(
    new Set(
      rolesString
        .replace(/[{}]/g, '')
        .split(/[~,]/)
        .map((r) => r.trim())
        .filter(Boolean)
    )
  )
}

export const parsePostgresArray = (pgArrayString) => {
  if (!pgArrayString || pgArrayString === '{}') return []
  return pgArrayString
    .replace(/[{}"]/g, '')
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item !== '')
}

export const countUniqueClients = (arrayString) => {
  const array = parsePostgresArray(arrayString)
  return new Set(array).size
}

export const sanitizeUID = (input) => {
  if (typeof input !== 'string') return ''
  let str = input.normalize('NFKC')
  str = str.replace(/[\s\u200B-\u200D\uFEFF]/gu, '')
  str = str.replace(/[^a-zA-Z0-9_-]/g, '')
  return str
}

export const isDocumentCredit = (model) => {
  return !model?.charge_uid && model?.child_document_type === 'Factura'
}

// Esta funcion transforma el string que recibimos del Backend  (00:00:00 AM) a (00:00 AM)
export const formatTimetoHHmm = (time) => {
  const timeRegex = /^\d{1,2}:\d{2}:\d{2} [APMapm]{2}$/
  if (!timeRegex.test(time)) {
    return time
  }
  return dayjs(time, 'hh:mm:ss A').format('hh:mm A')
}

export const formatActivityName = (activity_name) => {
  if (TASK_TYPES[activity_name]) {
    return TASK_TYPES[activity_name].shortName
  } else {
    return activity_name
  }
}

export const cutTextWithLineBreak = (text, firstLineLimit = 45, maxLength = 90) => {
  if (!text) return '-'

  const trimmed = text.substring(0, maxLength)

  if (trimmed.length <= firstLineLimit) {
    return trimmed
  }

  return trimmed.substring(0, firstLineLimit) + '\n' + trimmed.substring(firstLineLimit)
}

export function formatPercentSmart(value) {
  if (value === null || value === undefined) return '—'

  const num = Number(value)
  if (Number.isNaN(num)) return '—'

  return Number.isInteger(num) ? `${num}%` : `${num.toFixed(2)}%`
}

export const normalizeErrorDetails = (details) => {
  if (!details) return null

  if (typeof details === 'string') return details

  if (typeof details === 'object') {
    return details.message || 'Error desconocido'
  }

  return String(details)
}

export const toSafeNumber = (value) => {
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : 0
}

export const formatQuantity = (value) => {
  const numberValue = toSafeNumber(value)
  return Number.isInteger(numberValue) ? String(numberValue) : numberValue.toFixed(2)
}

export const clampPercent = (value) => Math.min(Math.max(toSafeNumber(value), 0), 100)

const NUMERIC_TEXT = /^[-+]?\d+([.,]\d+)?$/

export const escapeCsvValue = (value) => {
  const stringValue = String(value ?? '')
  const isFormulaLikeText =
    typeof value !== 'number' &&
    stringValue !== '-' &&
    !NUMERIC_TEXT.test(stringValue) &&
    /^[=+\-@\t\r\n]/.test(stringValue)
  const safeValue = isFormulaLikeText ? `'${stringValue}` : stringValue
  if (!/[",\n\r]/.test(safeValue)) return safeValue
  return `"${safeValue.replace(/"/g, '""')}"`
}

export const hasExportValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value)
  if (typeof value === 'boolean') return true
  if (Array.isArray(value)) return value.length > 0
  if (value && typeof value === 'object') return Object.keys(value).length > 0

  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()

  return !['', '-', '—', 'n/a', 'na', 'null', 'undefined'].includes(normalized)
}

export const sanitizeFilenamePart = (value, fallback = 'file') => {
  const filenamePart = String(value || '')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_')

  return filenamePart || fallback
}

export const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.top = '-1000px'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}
